import { showHttpError } from '@calendly/alert'
import { httpRequest } from '@calendly/http-tools'
import { I18n } from '@calendly/i18n'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { nanoid } from 'nanoid'

import { redirectTo } from 'actions/core/navigation'
import changeSet from 'core/change_set'
import { findWorkflowTemplate } from 'services/workflows/templates'
import { getScope as convertOwnerType } from 'services/workflows/url'

import { assignSendOptionsToTemplate, getDefaultActionConfiguration } from './send_options'

// Matches the same canonical 8-4-4-4-12 hex layout accepted by the server
// (Workflows::WorkflowUuid::CANONICAL_PATTERN). Validated before interpolating
// into API URLs to prevent client-side path traversal.
const CANONICAL_UUID_RE = /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i

export const requestOriginalWorkflow = createAsyncThunk(
  'workflows/builder/requestOriginal',
  async (
    { ownerId, ownerType, originalWorkflowUuid, workflowsExtractionEnabled },
    { rejectWithValue }
  ) => {
    // Reject non-strings (e.g. arrays produced by HTTP Parameter Pollution via qs
    // `duplicates: 'combine'`) and anything that isn't a canonical UUID v4 shape.
    if (typeof originalWorkflowUuid !== 'string' || !CANONICAL_UUID_RE.test(originalWorkflowUuid)) {
      return rejectWithValue({ message: 'invalid originalWorkflowUuid' })
    }

    const url = workflowsExtractionEnabled
      ? `/api/apps/workflows/workflows/${originalWorkflowUuid}`
      : `/api/${convertOwnerType(ownerType)}s/${ownerId}/workflows/${originalWorkflowUuid}`
    const [success, responseData] = await httpRequest({
      url,
      method: 'GET'
    })

    if (!success) {
      return rejectWithValue(responseData)
    }

    return responseData
  }
)

const submitWorkflow = createAsyncThunk(
  'workflows/builder/submitWorkflow',
  async (
    { workflow, listUrl, requestOptions, templateId, originalWorkflowUuid },
    { rejectWithValue, dispatch }
  ) => {
    const [success, responseData] = await httpRequest({
      data: {
        ...workflow,
        template_id: templateId,
        original_workflow_uuid: originalWorkflowUuid
      },
      ...requestOptions
    })

    if (success) {
      changeSet.reset()
      window.location.assign(listUrl)
      return null
    }

    switch (responseData.type) {
      case 'validation':
        return rejectWithValue(responseData.payload.errors)
      case 'policy':
        changeSet.reset()
        window.location.assign(listUrl)
        return null
      default:
        dispatch(showHttpError(responseData))
        return rejectWithValue({})
    }
  }
)

export const createWorkflow = (workflow, saveUrl, listUrl, templateId, originalWorkflowUuid) => (
  submitWorkflow({
    workflow,
    listUrl,
    requestOptions: {
      url: saveUrl,
      method: 'POST'
    },
    templateId,
    originalWorkflowUuid
  })
)

export const updateWorkflow = (workflow, saveUrl, listUrl) => (
  submitWorkflow({
    workflow,
    listUrl,
    requestOptions: {
      url: saveUrl,
      method: 'PATCH'
    }
  })
)

const normalizeKeys = (actions) => actions.map((action) => ({
  key: nanoid(5),
  ...action
}))

const normalizeWorkflow = ({
  workflow_rule: {
    workflow_actions: actions,
    ...restRuleProps
  },
  ...restWorkflowProps
}) => ({
  ...restWorkflowProps,
  workflow_rule: {
    ...restRuleProps,
    workflow_actions: normalizeKeys(actions)
  }
})

const slice = createSlice({
  name: 'workflows/builder',
  initialState: {
    workflow: {},
    actionToEdit: null
  },
  reducers: {
    clearWorkflowErrors: (state) => {
      state.workflow.errors = {}
    },
    setWorkflowWithParameters: (state, {
      payload: {
        triggerType,
        timeUnits,
        timeValue,
        workflowsAction,
        sendOptions
      }
    }) => {
      const [type, trigger, eventName] = triggerType.split('.')
      state.workflow = {
        type,
        workflow_rule: {
          trigger_type: trigger,
          event_name: eventName,
          time_value: timeValue,
          time_units: timeUnits,
          workflow_actions: [{
            key: nanoid(5),
            configuration: getDefaultActionConfiguration(workflowsAction, sendOptions)
          }]
        }
      }
    },
    setWorkflowFromApi: (state, { payload: workflow }) => {
      state.workflow = normalizeWorkflow(workflow)
      state.workflowLoadingState = undefined
    },
    setWorkflowTemplate: (state, {
      payload: {
        template,
        sendOptions
      }
    }) => {
      const {
        id: _,
        ...templateWorkflow
      } = template

      state.workflow = assignSendOptionsToTemplate(templateWorkflow, sendOptions)
    },

    openActionEditor: (state, { payload: actionToEdit }) => {
      state.actionToEdit = actionToEdit
    },

    closeActionEditor: (state) => {
      state.actionToEdit = null
    },

    setEventTypeIds: (state, { payload: { ids } }) => {
      state.workflow.event_type_ids = ids
    },

    setEventTypes: (state, { payload }) => {
      state.workflow.event_types = payload
    },

    setWorkflowLoadError: (state) => {
      state.workflowLoadingState = 'loaded_with_error'
    }
  },
  extraReducers: (builder) => {
    builder.addCase(submitWorkflow.rejected, (state, { payload: errors }) => {
      const { workflow_rule: ruleErrors, ...workflowError } = errors
      const { workflow_actions: actionErrors, ...ruleError } = (ruleErrors ? ruleErrors[0] : {})

      state.workflow.errors = workflowError
      state.workflow.workflow_rule.errors = ruleError

      state.workflow.workflow_rule.workflow_actions.forEach((workflowAction, index) => {
        workflowAction.errors = (actionErrors && actionErrors[index]) || {}
      })
    })
    builder.addCase(requestOriginalWorkflow.pending, (state) => {
      state.workflowLoadingState = 'loading'
    })
    builder.addCase(requestOriginalWorkflow.rejected, (state, { payload: error }) => {
      state.error = error
      state.workflowLoadingState = 'loaded_with_error'
    })
    builder.addCase(requestOriginalWorkflow.fulfilled, (state, { payload: workflow }) => {
      state.workflowLoadingState = 'loaded'
      state.workflow = {
        ...normalizeWorkflow(workflow),
        name: `${workflow.name} (${I18n.t('publisher.labels.cloned_suffix')})`
      }
    })
  }
})

const { actions: { setWorkflowTemplate } } = slice

export const loadWorkflowTemplate = ({ backUrl, templateId, isAppShellRebrand, ...rest }) => async (dispatch) => {
  const template = findWorkflowTemplate(templateId, isAppShellRebrand)
  if (!template) {
    return dispatch(redirectTo(backUrl))
  }

  return dispatch(setWorkflowTemplate({
    template,
    ...rest
  }))
}

export const {
  reducer,
  actions: {
    clearWorkflowErrors,
    openActionEditor,
    closeActionEditor,
    setEventTypeIds,
    setEventTypes,
    setWorkflowWithParameters,
    setWorkflowFromApi,
    setWorkflowLoadError
  }
} = slice
